import { useState } from 'react';
import { Menu, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAudioStore } from '@/stores/audioStore';
import { useUIStore } from '@/stores/uiStore';
import { useToast } from '@/hooks/useToast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Tooltip } from '@/components/ui/Tooltip';
import { ChangePasswordModal } from '@/components/auth/ChangePasswordModal';
import { UserMenu } from './components/UserMenu';
import './Topbar.scss';

export const Topbar = () => {
  const { user, logout } = useAuth();
  const audioEnabled = useAudioStore((s) => s.enabled);
  const toggleAudio = useAudioStore((s) => s.toggle);
  const openMobileSidebar = useUIStore((s) => s.openMobileSidebar);
  const navigate = useNavigate();
  const toast = useToast();
  // Confirm-before-logout: avoids the "oops misclick" problem. The dialog
  // lives at this level (not inside the menu) so a future second entry-
  // point — e.g. a session-timeout banner — can reuse the same wiring.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Change-password is an overlay action, not a route. Triggered from
  // the user menu's "Change password" item; modal state lives here so
  // closing the menu doesn't unmount the modal mid-edit.
  const [changePwOpen, setChangePwOpen] = useState(false);

  const performLogout = async () => {
    try {
      await logout();
      toast.success('Signed out.');
    } catch {
      // authStore.logout is built on authAPI.logout (which never throws),
      // so this catch should be dead code — but keep it so a future
      // refactor that re-introduces a throw doesn't break the flow.
      toast.info('Signed out locally. Server cleanup may be incomplete.');
    } finally {
      // Always navigate, regardless of API outcome. The local state is
      // already clean by this point (tokens + user cleared in the store).
      navigate('/login', { replace: true });
    }
  };

  return (
    <header className="topbar" role="banner">
      <div className="topbar__left">
        <button
          type="button"
          className="topbar__menu-btn"
          onClick={openMobileSidebar}
          aria-label="Open menu"
        >
          <Menu size={20} aria-hidden="true" />
        </button>
      </div>

      <div className="topbar__right">
        <Tooltip label={audioEnabled ? 'Mute alert chimes' : 'Unmute alert chimes'} placement="bottom">
          <button
            type="button"
            className="topbar__icon-btn"
            onClick={toggleAudio}
            aria-label={audioEnabled ? 'Mute alerts' : 'Unmute alerts'}
          >
            {audioEnabled ? <Volume2 size={18} aria-hidden="true" /> : <VolumeX size={18} aria-hidden="true" />}
          </button>
        </Tooltip>

        {/* User-menu dropdown — Profile / Change password (opens modal) /
            Billing on top; Sign-out at the bottom behind a divider + the
            ConfirmDialog gate. */}
        {user && (
          <UserMenu
            user={user}
            onChangePasswordClick={() => setChangePwOpen(true)}
            onSignOutClick={() => setConfirmOpen(true)}
          />
        )}
      </div>

      <ChangePasswordModal
        open={changePwOpen}
        onClose={() => setChangePwOpen(false)}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={performLogout}
        tone="warning"
        title="Sign out?"
        description={
          <p>
            You'll be returned to the sign-in screen. Cameras and devices keep streaming —
            this only ends your dashboard session.
          </p>
        }
        confirmLabel="Sign out"
        cancelLabel="Stay signed in"
      />
    </header>
  );
};
